import { CircleCheck, CircleX, Info } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { isResultMessage, postResult } from './channel';
import { DENIED_RESULTS, isConnectResult, resultCopy, serviceProvider } from './results';

/** The complete page closes itself this long after it reported (Figma B3). */
export const COMPLETE_CLOSE_MS = 3_000;

interface CompletePageProps {
  /** The query's `service`: a lowercase service id, or empty when the CRM could not tell. */
  service?: string;
  /** The query's `result`: a Connect result code. */
  result?: string;
}

type Outcome = 'connected' | 'stopped' | 'failed';

function outcomeOf(valid: boolean, result: string): Outcome {
  if (!valid) return 'failed';
  if (result === 'ok') return 'connected';
  return DENIED_RESULTS.includes(result) ? 'stopped' : 'failed';
}

/**
 * Where every Connect ends (`/settings/integrations/complete?service=…&result=…`), outside the app
 * shell, so a tab with no restored session still reports. It posts the values that pass the shape
 * checks on the channel, says how the Connect ended, and closes itself (CC D16).
 */
export function CompletePage({ service = '', result = '' }: CompletePageProps) {
  // Never suspends: the report goes out whether or not the copy has loaded.
  const { t, ready } = useTranslation('integrations', { useSuspense: false });
  const reported = useRef(false);

  useEffect(() => {
    if (!reported.current) {
      reported.current = true;
      postResult({ service, result });
    }
    const timer = setTimeout(() => {
      window.close();
    }, COMPLETE_CLOSE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [service, result]);

  const valid = isResultMessage({ service, result });
  const outcome = outcomeOf(valid, result);
  // By the service's provider, as the card shows it (GS §9's Workspace line for Google).
  const copy =
    valid && isConnectResult(result)
      ? resultCopy(result, serviceProvider(service))
      : resultCopy('unknown');
  const Icon = outcome === 'connected' ? CircleCheck : outcome === 'stopped' ? Info : CircleX;

  if (!ready) return <main className="min-h-screen bg-background" />;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <div
        className="w-full max-w-md rounded-lg border bg-card p-6 text-center"
        data-outcome={outcome}
      >
        <Icon
          aria-hidden
          className={
            outcome === 'connected'
              ? 'mx-auto size-8 text-emerald-600'
              : outcome === 'stopped'
                ? 'mx-auto size-8 text-muted-foreground'
                : 'mx-auto size-8 text-destructive'
          }
        />
        <h1 className="mt-3 text-lg font-semibold text-foreground">
          {outcome === 'connected'
            ? t('complete.connected', 'Connected')
            : outcome === 'stopped'
              ? t('complete.stopped', 'Not connected')
              : t('complete.failed', 'Connection failed')}
        </h1>
        {outcome !== 'connected' && (
          <p className="mt-2 text-sm text-muted-foreground">{t(copy.key, copy.defaultValue)}</p>
        )}
        <p className="mt-4 text-sm text-foreground">
          {t(
            'complete.return',
            'You can close this tab and return to Investbanq — the connector and any waiting AI CIO request have been updated',
          )}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          {t('complete.closing', 'This tab closes automatically in 3 seconds')}
        </p>
      </div>
    </main>
  );
}
